/**
 * Effects Calculator - Calculates combined bonuses from selected pacts
 * 
 * This replicates the logic from the OSRS Wiki Demonic Pacts Planner effects toolbar.
 * Effects with the same name are combined/summed, with sources tracked for display.
 */

import type { GraphNode } from './types';

export interface EffectSource {
	nodeId: string;
	nodeSize: string;
	value: number | string;
	isSecondary: boolean;
	secondaryName?: string;
}

export interface EffectSummary {
	effectName: string;
	displayName: string;
	totalValue: number;
	values: Array<number | string>;
	sources: EffectSource[];
	primaryNode: GraphNode;
}

function shouldIncludeEffectValue(value: number | string): boolean {
	if (typeof value === 'number') return value > 0;
	return value.trim().length > 0;
}

function numericEffectValue(value: number | string): number {
	return typeof value === 'number' ? value : 0;
}

function addEffectSummary(
	effectsMap: Map<string, EffectSummary>,
	node: GraphNode,
	effectName: string,
	effectValue: number | string,
	isSecondary: boolean
): void {
	if (!shouldIncludeEffectValue(effectValue)) return;

	const numericValue = numericEffectValue(effectValue);
	const existing = effectsMap.get(effectName);

	if (existing) {
		existing.values.push(effectValue);
		existing.totalValue += numericValue;
		existing.sources.push({
			nodeId: node.id,
			nodeSize: node.size || 'node_minor',
			value: effectValue,
			isSecondary,
			secondaryName: isSecondary ? effectName : undefined
		});

		if (node.label && node.label.length < existing.displayName.length) {
			existing.displayName = node.label;
			existing.primaryNode = node;
		}
		return;
	}

	effectsMap.set(effectName, {
		effectName,
		displayName: node.label || node.id,
		totalValue: numericValue,
		values: [effectValue],
		sources: [{
			nodeId: node.id,
			nodeSize: node.size || 'node_minor',
			value: effectValue,
			isSecondary,
			secondaryName: isSecondary ? effectName : undefined
		}],
		primaryNode: node
	});
}

/**
 * Calculate current effects from selected nodes
 * 
 * Returns an array of EffectSummary objects, one per unique effect type.
 * Effects with the same name are combined, values are summed.
 */
export function calculateCurrentEffects(
	selectedNodeIds: string[],
	nodes: Record<string, GraphNode>
): EffectSummary[] {
	const effectsMap = new Map<string, EffectSummary>();

	for (const nodeId of selectedNodeIds) {
		const node = nodes[nodeId];
		if (!node) continue;

		// Handle primary effect
		if (node.effect) {
			addEffectSummary(effectsMap, node, node.effect.name, node.effect.value, false);
		}

		// Handle secondary effect (for nodes with multiple effects)
		if ((node as any).secondary_effect) {
			const secondary = (node as any).secondary_effect;
			addEffectSummary(effectsMap, node, secondary.name, secondary.value, true);
		}
	}

	return Array.from(effectsMap.values());
}

/**
 * Format a pact description by replacing # with the total value
 * and converting <col=*> tags to styled spans
 */
export function formatEffectDescription(description: string, totalValue: number): { title: string; body: string } {
	if (!description) {
		return { title: `+${totalValue}`, body: '' };
	}

	// Split by double newline to separate title from body
	const parts = description.split('\n\n');
	let title = parts[0];
	const body = parts.slice(1).join('\n\n');

	// Replace # with the total value
	title = title.replace(/#/g, String(totalValue));

	// Convert <col=*> tags to styled spans
	title = title.replace(/<col=([^>]+)>([^<]*)<\/col>/g, '<span class="colored-text" style="color: $1;">$2</span>');

	return { title, body };
}

/**
 * Get effect summaries sorted by total value (descending)
 */
export function getSortedEffects(
	selectedNodeIds: string[],
	nodes: Record<string, GraphNode>
): EffectSummary[] {
	const effects = calculateCurrentEffects(selectedNodeIds, nodes);
	return effects.sort((a, b) => b.totalValue - a.totalValue);
}
